import {
  DOC_STATUS_LABEL,
  type DocStatus,
  type Lot,
  type Page,
  type ProductListRow,
} from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type Ref } from "react";
import { EntityLookup, productSource } from "@/components/lookup";
import { Alert, Badge, Button, Input, Select } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";
import { DOC_TONE, dateBR, qty, useWarehouses } from "./lib";

export function DocStatusBadge({ status }: { status: DocStatus }) {
  return <Badge tone={DOC_TONE[status]}>{DOC_STATUS_LABEL[status]}</Badge>;
}

/**
 * Produto por código, descrição ou código de barras (digitação + lupa). Na venda, `sale` traz
 * o depósito e o cliente para mostrar o disponível e o preço da tabela.
 */
export function ProductPicker({
  value,
  label,
  onChange,
  disabled,
  sale,
  id,
  inputRef,
  onSelected,
  invalid,
}: {
  value: string;
  label: string;
  onChange: (id: string, label: string, product?: ProductListRow) => void;
  disabled?: boolean;
  sale?: { warehouseId?: string; customerId?: string };
  id?: string;
  inputRef?: Ref<HTMLInputElement>;
  onSelected?: (product: ProductListRow) => void;
  invalid?: boolean;
}) {
  const warehouseId = sale?.warehouseId;
  const customerId = sale?.customerId;
  const source = useMemo(
    () =>
      productSource({
        ...(warehouseId ? { warehouseId } : {}),
        ...(customerId ? { customerId } : {}),
      }),
    [warehouseId, customerId],
  );
  return (
    <EntityLookup
      source={source}
      value={value}
      label={label}
      onChange={onChange}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(id !== undefined ? { id } : {})}
      {...(inputRef !== undefined ? { inputRef } : {})}
      {...(onSelected !== undefined ? { onSelected } : {})}
      {...(invalid ? { "aria-invalid": true } : {})}
    />
  );
}

export function WarehouseSelect({
  value,
  onChange,
  includeInactive,
  allOption,
  disabled,
  excludeVendor,
}: {
  value: string;
  onChange: (id: string) => void;
  includeInactive?: boolean;
  allOption?: string;
  disabled?: boolean;
  /** estoque de vendedor só se movimenta por carga/devolução/acerto: fora das escolhas diretas */
  excludeVendor?: boolean;
}) {
  const whs = useWarehouses();
  const selected = whs.data?.find((w) => w.id === value);
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      // nome longo: o nome completo aparece ao passar o mouse
      title={selected ? `${selected.name}${selected.isDefault ? " (padrão)" : ""}` : allOption}
    >
      <option value="">{allOption ?? "Selecione"}</option>
      {whs.data
        ?.filter((w) => includeInactive || w.status === "active")
        .filter((w) => !excludeVendor || w.kind !== "vendedor" || w.id === value)
        .map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
            {w.isDefault ? " (padrão)" : ""}
          </option>
        ))}
    </Select>
  );
}

/** Lotes do produto (com saldo no depósito, quando informado). */
export function LotSelect({
  productId,
  warehouseId,
  value,
  onChange,
  emptyLabel = "Selecione o lote",
  onlyWithBalance = true,
}: {
  productId: string;
  warehouseId?: string;
  value: string;
  onChange: (id: string) => void;
  emptyLabel?: string;
  onlyWithBalance?: boolean;
}) {
  const params = new URLSearchParams({ productId, comSaldo: onlyWithBalance ? "1" : "0" });
  if (warehouseId) params.set("warehouseId", warehouseId);
  const lots = useQuery({
    queryKey: ["estoque", "lotes", params.toString()],
    queryFn: () => apiFetch<Page<Lot>>(`/estoque/lotes?${params.toString()}`),
    enabled: productId !== "",
  });
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{emptyLabel}</option>
      {lots.data?.items.map((l) => (
        <option key={l.id} value={l.id}>
          {l.code} · val. {dateBR(l.expiresOn)} · saldo {qty(l.balance)}
        </option>
      ))}
    </Select>
  );
}

/** Botão de estorno com motivo obrigatório. */
export function ReverseAction({
  label = "Estornar",
  description,
  confirmLabel = "Confirmar estorno",
  pending,
  error,
  onConfirm,
}: {
  label?: string;
  description?: string;
  confirmLabel?: string;
  pending: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!open)
    return (
      <Button variant="danger" size="sm" onClick={() => setOpen(true)}>
        {label}
      </Button>
    );
  return (
    <div className="space-y-3 rounded-lg border border-destructive/30 bg-destructive-soft/50 p-4">
      <p className="text-sm">
        {description ??
          "O estorno lança movimentos inversos de tudo que este documento movimentou. O documento fica marcado como estornado (não é apagado)."}
      </p>
      <Input
        placeholder="Motivo"
        aria-label="Motivo do estorno"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-2">
        <Button
          variant="danger"
          size="sm"
          disabled={pending || reason.trim().length < 3}
          onClick={() => onConfirm(reason.trim())}
        >
          {confirmLabel}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
          Voltar
        </Button>
      </div>
      {Boolean(error) && <Alert tone="error">{errorMessage(error)}</Alert>}
    </div>
  );
}
